/**
 * <product-form> — keeps the add-to-cart form's variant in sync with the option selectors.
 *
 * Expects:
 *   - a <script type="application/json" data-variants> child with the product's variants (Liquid `| json`)
 *   - one <savette-selector name="options[Name]"> per option (rendered with snippets/selector-item.liquid),
 *     inside this element — the price and the option rows are its children, not the buy box's
 *   - no option selectors at all is normal: a single-variant product keeps the variant the server chose
 *   - <input name="id"> for the selected variant, [data-price] and [data-add-to-cart] targets
 *   - optionally a <script type="application/json" data-variant-labels> map of variant id to its quiet
 *     label ("Exclusive", or "" for none), and a [data-product-label] element in the same section
 *   - optionally a <script type="application/json" data-variant-preorder> map of variant id to
 *     whether its color is on pre-order, and an <input name="properties[Note]" data-preorder-property>
 *     in the form (SAVETTE-70)
 *   - optionally a [data-notify-me] button, the Notify Me under Sold out (SAVETTE-70)
 *
 * On any selector change the matching variant is resolved, the hidden id, price and label update
 * (labels are per colour, U17), the URL's ?variant= parameter is replaced (so the page can be shared and refreshed on the same variant), and
 * unavailable options are marked.
 *
 * Pre-order and Notify Me are per color too (SAVETTE-70). A pre-order color that can be sold reads
 * Pre-order and sends the Note: Pre-order line item property (the input is enabled only then, so a
 * disabled one is never posted); a color that cannot be sold reads Sold out, pre-order or not, and
 * shows Notify Me.
 *
 * Adding posts to Shopify's cart API and asks for the bag drawer's markup in the same request, so
 * the drawer that opens and the cart it describes come from the same moment on the server. Without
 * JavaScript the form posts normally and the shopper lands on the cart page.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    // a malformed price map must never take the form down with it; formatMoney covers the gap
    try {
      this.prices = JSON.parse(this.querySelector('script[data-variant-prices]')?.textContent || '{}');
    } catch {
      this.prices = {};
    }
    // the same for the label map: without it the label simply keeps what the server rendered
    try {
      this.labels = JSON.parse(this.querySelector('script[data-variant-labels]')?.textContent || '{}');
    } catch {
      this.labels = {};
    }
    // and for the pre-order map: without it no color is on pre-order
    try {
      this.preorder = JSON.parse(this.querySelector('script[data-variant-preorder]')?.textContent || '{}');
    } catch {
      this.preorder = {};
    }
    this.preorderInput = this.querySelector('input[data-preorder-property]');
    this.notifyButton = this.querySelector('[data-notify-me]');
    // the label sits above the title, outside this element, in the same section
    this.labelEl = this.closest('[data-section-id]')?.querySelector('[data-product-label]');
    this.idInput = this.querySelector('input[name="id"]');
    this.priceEl = this.querySelector('[data-price]');
    this.submit = this.querySelector('[data-add-to-cart]');
    this.strings = JSON.parse(this.dataset.strings || '{}');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.form?.addEventListener('submit', this.onSubmit);
    this.addEventListener('selector:change', (event) => {
      // keep the option legend ("Color: Black") in step with the selected item
      const legendValue = event.target.closest('.product__option')?.querySelector('[data-option-value]');
      if (legendValue && event.detail.value != null) legendValue.textContent = event.detail.value;
      this.update();
    });
    this.update();
  }

  get optionSelectors() {
    return [...this.querySelectorAll('savette-selector[name^="options["]')];
  }

  update() {
    const selectors = this.optionSelectors;
    // A product with no options has nothing to resolve: the server already rendered the only
    // variant, and guessing from an empty selection would wrongly call it unavailable. Today's
    // catalog is entirely single-variant products, so this is the common path, not the edge case.
    if (selectors.length === 0) return;

    const chosen = selectors.map((s) => s.value);
    const variant = this.variants.find(
      (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i]),
    );
    if (variant) {
      this.idInput.value = variant.id;
      if (this.priceEl) this.priceEl.textContent = this.prices[variant.id] ?? this.formatMoney(variant.price);
      const label = this.labels[variant.id];
      if (this.labelEl && typeof label === 'string') {
        this.labelEl.textContent = label;
        this.labelEl.hidden = !label;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    const available = Boolean(variant && variant.available);
    const preorder = available && this.preorder[variant.id] === true;
    if (this.submit) {
      this.submit.disabled = !variant || !available;
      this.submit.textContent = !variant
        ? this.strings.unavailable
        : !available
          ? this.strings.sold_out
          : preorder
            ? this.strings.preorder
            : this.strings.add_to_cart;
    }
    if (this.preorderInput) this.preorderInput.disabled = !preorder;
    // Notify Me is for a color that exists and is sold out, not for a combination that does not exist
    if (this.notifyButton) this.notifyButton.hidden = !variant || available;
    this.dispatchEvent(new CustomEvent('product-form:variant', { bubbles: true, detail: { variant } }));
  }

  /** Add to the cart without leaving the page, then let the bag drawer take over. */
  onSubmit = async (event) => {
    event.preventDefault();
    if (this.submit?.disabled) return;
    this.submit?.setAttribute('aria-busy', 'true');
    try {
      const body = new FormData(this.form);
      // ask for the bag drawer's markup alongside the add, so it renders the cart this add
      // produced; the drawer knows its own section id, which need not be its type
      const drawer = document.querySelector('cart-drawer');
      body.append('sections', drawer?.dataset.section ?? 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
        cache: 'no-store',
      });
      const result = await response.json();
      if (!response.ok) {
        this.form.submit(); // let the server render the error the usual way
        return;
      }
      document.dispatchEvent(
        new CustomEvent('cart:added', { detail: { item: result, sections: result.sections } }),
      );
    } finally {
      this.submit?.removeAttribute('aria-busy');
    }
  };

  // Fallback only, for a variant missing from data-variant-prices: the brand price format as
  // snippets/money.liquid renders it ("$2290", not "$2,290.00") in the shop's base money format.
  formatMoney(cents) {
    const value = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\w*\s*\}\}/, value);
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
